import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  shellApi,
  type ChatMessageDto,
  type ChatTreatmentStatus,
  type ConversationDto,
  type PendingConversationDto,
} from "../../api/shellApi";
import { useAuth } from "../../auth/AuthProvider";
import { getRealtimeSocket } from "../../realtime/socketService";
import {
  playChatNotificationSound,
  unlockChatNotificationSound,
} from "./chatNotificationSound";
import { isOpenTreatmentStatus } from "./chatTreatmentConstants";

export type ChatToast = {
  open: boolean;
  sender: string;
  content: string;
  conversationId: string | null;
};

type ChatNotifyPayload = {
  conversationId: string;
  messageId: string;
  preview?: string;
  senderUserId?: string;
  senderName?: string;
};

type ChatContextValue = {
  canView: boolean;
  canSend: boolean;
  canManageTreatment: boolean;
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  conversations: ConversationDto[];
  pending: PendingConversationDto[];
  unreadBadge: number;
  treatmentOpenBadge: number;
  activeConversationId: string | null;
  setActiveConversationId: (id: string | null) => void;
  messages: ChatMessageDto[];
  replyTo: ChatMessageDto | null;
  setReplyTo: (msg: ChatMessageDto | null) => void;
  highlightMessageId: string | null;
  toast: ChatToast;
  dismissToast: () => void;
  openFromToast: () => void;
  reloadConversations: () => Promise<void>;
  reloadPending: () => Promise<void>;
  openConversation: (id: string, focusMessageId?: string) => Promise<void>;
  sendMessage: (content: string) => Promise<void>;
  toggleReaction: (messageId: string, emoji: string) => Promise<void>;
  deleteMessage: (messageId: string) => Promise<void>;
  markUnreadFromMessage: (messageId: string) => Promise<void>;
  setTreatment: (messageId: string, status: ChatTreatmentStatus | null) => Promise<void>;
  scrollToMessage: (messageId: string) => void;
  markActiveRead: () => Promise<void>;
  currentUserId: string | null;
  typingLabel: string;
  emitTyping: (isTyping: boolean) => void;
  treatmentQueueTick: number;
};

const ChatContext = createContext<ChatContextValue | null>(null);

function conversationTitle(conv: ConversationDto, currentUserId: string | null): string {
  if (conv.name?.trim()) return conv.name.trim();
  const others = (conv.members ?? []).filter(
    (m) => m.userId !== currentUserId && m.approvalStatus === "approved",
  );
  if (others.length > 0) return others.map((m) => m.displayName).join(", ");
  return conv.isGroup ? "קבוצה" : "שיחה";
}

export function conversationDisplayName(
  conv: ConversationDto,
  currentUserId: string | null,
): string {
  return conversationTitle(conv, currentUserId);
}

export function ChatProvider({ children }: { children: ReactNode }) {
  const auth = useAuth();
  const [granted, setGranted] = useState<string[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [conversations, setConversations] = useState<ConversationDto[]>([]);
  const [pending, setPending] = useState<PendingConversationDto[]>([]);
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessageDto[]>([]);
  const [replyTo, setReplyTo] = useState<ChatMessageDto | null>(null);
  const [highlightMessageId, setHighlightMessageId] = useState<string | null>(null);
  const [treatmentQueueTick, setTreatmentQueueTick] = useState(0);
  const [treatmentOpenBadge, setTreatmentOpenBadge] = useState(0);
  const [toast, setToast] = useState<ChatToast>({
    open: false,
    sender: "",
    content: "",
    conversationId: null,
  });
  const [typingLabel, setTypingLabel] = useState("");

  const isOpenRef = useRef(isOpen);
  const activeRef = useRef(activeConversationId);
  useEffect(() => {
    isOpenRef.current = isOpen;
  }, [isOpen]);
  useEffect(() => {
    activeRef.current = activeConversationId;
  }, [activeConversationId]);

  const canView = granted.includes("chat.view");
  const canSend = granted.includes("chat.send");
  const canManageTreatment =
    auth.user?.platformRoles?.includes("super_admin") === true ||
    auth.user?.platformRoles?.includes("technical_staff") === true;
  const currentUserId = auth.user?.id ?? null;

  const unreadBadge = useMemo(() => {
    const unreadConvs = conversations.filter((c) => (c.unreadCount ?? 0) > 0).length;
    return unreadConvs + pending.length;
  }, [conversations, pending]);

  const reloadConversations = useCallback(async () => {
    if (!canView) return;
    try {
      const items = await shellApi.listConversations();
      setConversations(items);
    } catch (error) {
      console.error("Failed to load chat conversations", error);
    }
  }, [canView]);

  const reloadPending = useCallback(async () => {
    if (!canView) return;
    const items = await shellApi.listPendingConversations();
    setPending(items);
  }, [canView]);

  const reloadTreatmentBadge = useCallback(async () => {
    if (!canManageTreatment) {
      setTreatmentOpenBadge(0);
      return;
    }
    try {
      const items = await shellApi.listTreatmentQueue(null);
      setTreatmentOpenBadge(items.filter((i) => isOpenTreatmentStatus(i.treatmentStatus)).length);
    } catch {
      setTreatmentOpenBadge(0);
    }
  }, [canManageTreatment]);

  useEffect(() => {
    void shellApi
      .listGrantedPermissions()
      .then(setGranted)
      .catch(() => setGranted([]));
  }, [auth.activeTenant?.tenantId]);

  useEffect(() => {
    if (!canView) return;
    unlockChatNotificationSound();
    void reloadConversations().catch(() => undefined);
    void reloadPending().catch(() => undefined);
  }, [canView, reloadConversations, reloadPending, auth.activeTenant?.tenantId]);

  useEffect(() => {
    void reloadTreatmentBadge().catch(() => undefined);
  }, [reloadTreatmentBadge, treatmentQueueTick, auth.activeTenant?.tenantId]);

  const markActiveRead = useCallback(async () => {
    const id = activeRef.current;
    if (!id) return;
    await shellApi.markConversationRead(id);
    setConversations((prev) =>
      prev.map((c) => (c.id === id ? { ...c, unreadCount: 0 } : c)),
    );
  }, []);

  const scrollToMessage = useCallback((messageId: string) => {
    setHighlightMessageId(messageId);
    window.setTimeout(() => {
      document.getElementById(`chat-msg-${messageId}`)?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }, 50);
    window.setTimeout(() => setHighlightMessageId(null), 1600);
  }, []);

  const openConversation = useCallback(
    async (id: string, focusMessageId?: string) => {
      setActiveConversationId(id);
      setIsOpen(true);
      setReplyTo(null);
      const items = await shellApi.listChatMessages(id);
      setMessages(items);
      getRealtimeSocket().emit("join_conversation", { conversationId: id });
      await shellApi.markConversationRead(id);
      setConversations((prev) =>
        prev.map((c) => (c.id === id ? { ...c, unreadCount: 0 } : c)),
      );
      if (focusMessageId) {
        window.setTimeout(() => scrollToMessage(focusMessageId), 80);
      }
    },
    [scrollToMessage],
  );

  const sendMessage = useCallback(
    async (content: string) => {
      const id = activeRef.current;
      if (!id || !content.trim()) return;
      const msg = await shellApi.sendChatMessage(id, {
        content: content.trim(),
        replyToMessageId: replyTo?.id ?? null,
      });
      setMessages((prev) => (prev.some((m) => m.id === msg.id) ? prev : [...prev, msg]));
      setReplyTo(null);
      void reloadConversations().catch(() => undefined);
    },
    [reloadConversations, replyTo],
  );

  const toggleReaction = useCallback(
    async (messageId: string, emoji: string) => {
      const id = activeRef.current;
      if (!id) return;
      const result = await shellApi.toggleChatReaction(id, messageId, emoji);
      setMessages((prev) =>
        prev.map((m) =>
          m.id === messageId ? { ...m, reactions: result.reactions } : m,
        ),
      );
    },
    [],
  );

  const deleteMessage = useCallback(async (messageId: string) => {
    const id = activeRef.current;
    if (!id) return;
    await shellApi.deleteChatMessage(id, messageId);
    setMessages((prev) => prev.filter((m) => m.id !== messageId));
    void reloadConversations().catch(() => undefined);
  }, [reloadConversations]);

  const markUnreadFromMessage = useCallback(
    async (messageId: string) => {
      const id = activeRef.current;
      if (!id) return;
      const result = await shellApi.markConversationUnread(id, messageId);
      setActiveConversationId(null);
      setReplyTo(null);
      setConversations((prev) =>
        prev.map((c) =>
          c.id === id ? { ...c, unreadCount: result.unreadCount } : c,
        ),
      );
      void reloadConversations().catch(() => undefined);
    },
    [reloadConversations],
  );

  const setTreatment = useCallback(
    async (messageId: string, status: ChatTreatmentStatus | null) => {
      const id = activeRef.current;
      if (!id) return;
      const updated = await shellApi.setChatMessageTreatment(id, messageId, status);
      setMessages((prev) =>
        prev.map((m) => (m.id === messageId ? { ...m, ...updated } : m)),
      );
      setTreatmentQueueTick((n) => n + 1);
    },
    [],
  );

  const emitTyping = useCallback((isTyping: boolean) => {
    const id = activeRef.current;
    if (!id) return;
    getRealtimeSocket().emit("chat:typing", { conversationId: id, isTyping });
  }, []);

  useEffect(() => {
    if (!canView) return;
    const socket = getRealtimeSocket();

    const onNotify = (payload: ChatNotifyPayload) => {
      if (!payload?.conversationId) return;
      const viewing =
        isOpenRef.current && activeRef.current === payload.conversationId;
      setConversations((prev) => {
        const exists = prev.some((c) => c.id === payload.conversationId);
        if (!exists) {
          void reloadConversations().catch(() => undefined);
          return prev;
        }
        return prev.map((c) =>
          c.id === payload.conversationId && !viewing
            ? { ...c, unreadCount: (c.unreadCount ?? 0) + 1 }
            : c,
        );
      });
      if (!viewing) {
        playChatNotificationSound();
        setToast({
          open: true,
          sender: payload.senderName || "הודעה חדשה",
          content: payload.preview || "",
          conversationId: payload.conversationId,
        });
      } else {
        void shellApi.markConversationRead(payload.conversationId).catch(() => undefined);
      }
    };

    const onMessage = (msg: ChatMessageDto) => {
      if (!msg?.conversationId) return;
      if (activeRef.current === msg.conversationId) {
        setMessages((prev) => (prev.some((m) => m.id === msg.id) ? prev : [...prev, msg]));
        void shellApi.markConversationRead(msg.conversationId).catch(() => undefined);
      }
      void reloadConversations().catch(() => undefined);
    };

    const onMessageUpdated = (msg: ChatMessageDto) => {
      if (activeRef.current !== msg.conversationId) return;
      setMessages((prev) => prev.map((m) => (m.id === msg.id ? { ...m, ...msg } : m)));
    };

    const onMessageDeleted = (payload: { id: string; conversationId: string }) => {
      if (activeRef.current !== payload.conversationId) return;
      setMessages((prev) =>
        prev.map((m) =>
          m.id === payload.id
            ? m
            : m.replyToMessageId === payload.id && m.replySnapshot
              ? {
                  ...m,
                  replySnapshot: { ...m.replySnapshot, isDeleted: true },
                }
              : m,
        ).filter((m) => m.id !== payload.id),
      );
    };

    const onReaction = (payload: {
      messageId: string;
      conversationId: string;
      reactions: Array<{ emoji: string; userId: string }>;
    }) => {
      if (activeRef.current !== payload.conversationId) return;
      setMessages((prev) =>
        prev.map((m) =>
          m.id === payload.messageId ? { ...m, reactions: payload.reactions } : m,
        ),
      );
    };

    const onPeerRead = (payload: {
      conversationId: string;
      readAt: string;
      readByUserId: string;
    }) => {
      if (activeRef.current !== payload.conversationId) return;
      if (payload.readByUserId === currentUserId) return;
      const readAtMs = new Date(payload.readAt).getTime();
      setMessages((prev) =>
        prev.map((m) => {
          if (m.senderUserId !== currentUserId) return m;
          if (new Date(m.createdAt).getTime() <= readAtMs) {
            return { ...m, deliveryStatus: "read" };
          }
          return m;
        }),
      );
    };

    const onConversation = () => {
      void reloadConversations().catch(() => undefined);
    };

    const onPending = () => {
      void reloadPending().catch(() => undefined);
    };

    const onTreatmentQueue = () => {
      setTreatmentQueueTick((n) => n + 1);
    };

    const onTyping = (payload: {
      conversationId: string;
      userId: string;
      isTyping: boolean;
    }) => {
      if (payload.userId === currentUserId) return;
      if (activeRef.current !== payload.conversationId) return;
      setTypingLabel(payload.isTyping ? "מקליד/ה…" : "");
      if (!payload.isTyping) return;
      window.setTimeout(() => setTypingLabel(""), 3000);
    };

    socket.on("chat:notify", onNotify);
    socket.on("chat:message", onMessage);
    socket.on("chat:message:updated", onMessageUpdated);
    socket.on("chat:message:deleted", onMessageDeleted);
    socket.on("chat:reaction", onReaction);
    socket.on("chat:peer_read", onPeerRead);
    socket.on("chat:conversation", onConversation);
    socket.on("chat:pending", onPending);
    socket.on("chat:treatment_queue_updated", onTreatmentQueue);
    socket.on("chat:typing", onTyping);

    return () => {
      socket.off("chat:notify", onNotify);
      socket.off("chat:message", onMessage);
      socket.off("chat:message:updated", onMessageUpdated);
      socket.off("chat:message:deleted", onMessageDeleted);
      socket.off("chat:reaction", onReaction);
      socket.off("chat:peer_read", onPeerRead);
      socket.off("chat:conversation", onConversation);
      socket.off("chat:pending", onPending);
      socket.off("chat:treatment_queue_updated", onTreatmentQueue);
      socket.off("chat:typing", onTyping);
    };
  }, [canView, currentUserId, reloadConversations, reloadPending]);

  useEffect(() => {
    const prev = activeRef.current;
    return () => {
      if (prev) {
        getRealtimeSocket().emit("leave_conversation", { conversationId: prev });
      }
    };
  }, [activeConversationId]);

  const value = useMemo<ChatContextValue>(
    () => ({
      canView,
      canSend,
      canManageTreatment,
      isOpen,
      setIsOpen,
      conversations,
      pending,
      unreadBadge,
      treatmentOpenBadge,
      activeConversationId,
      setActiveConversationId,
      messages,
      replyTo,
      setReplyTo,
      highlightMessageId,
      toast,
      dismissToast: () => setToast((t) => ({ ...t, open: false })),
      openFromToast: () => {
        const id = toast.conversationId;
        setToast((t) => ({ ...t, open: false }));
        if (id) void openConversation(id);
        else setIsOpen(true);
      },
      reloadConversations,
      reloadPending,
      openConversation,
      sendMessage,
      toggleReaction,
      deleteMessage,
      markUnreadFromMessage,
      setTreatment,
      scrollToMessage,
      markActiveRead,
      currentUserId,
      typingLabel,
      emitTyping,
      treatmentQueueTick,
    }),
    [
      canView,
      canSend,
      canManageTreatment,
      isOpen,
      conversations,
      pending,
      unreadBadge,
      treatmentOpenBadge,
      activeConversationId,
      messages,
      replyTo,
      highlightMessageId,
      toast,
      reloadConversations,
      reloadPending,
      openConversation,
      sendMessage,
      toggleReaction,
      deleteMessage,
      markUnreadFromMessage,
      setTreatment,
      scrollToMessage,
      markActiveRead,
      currentUserId,
      typingLabel,
      emitTyping,
      treatmentQueueTick,
    ],
  );

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
}

export function useChat(): ChatContextValue {
  const ctx = useContext(ChatContext);
  if (!ctx) throw new Error("useChat must be used within ChatProvider");
  return ctx;
}
